import { useCallback, useState } from "react";
import { DeleteConfirmModal } from "../../components/modal/DeleteConfirmModal.jsx";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm.js";
import { DeviceSidebar, DevicesEmptyState } from "./components/DeviceSidebar.jsx";
import { DeviceDetailPanel } from "./components/DeviceDetailPanel.jsx";
import { CreateDeviceModal } from "./components/CreateDeviceModal.jsx";
import { AssignDeviceAccountModal, AssignExistingDeviceModal } from "./components/AssignDeviceModals.jsx";

export function DeviceManager({
  items,
  allItems,
  loading,
  search,
  setSearch,
  selected,
  selectedId,
  setSelectedId,
  addDevice,
  saveDevice,
  removeDevice,
  deassignDevice,
  assignDevice,
  assignExistingDevice,
  unassignedDevices = [],
  accounts = [],
  saving,
  getAccountName,
  getDeviceTypeName,
  fixedAccountName = null,
  emptyHint,
  emptyTitle = "No devices yet",
  fixedAccountId = null,
  deviceTypes = [],
  selectedDeviceType = null,
  sims = [],
  selectedSim = null,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignExistingOpen, setAssignExistingOpen] = useState(false);
  const { requestDelete, deleteModalProps } = useDeleteConfirm();

  const openCreate = useCallback(() => {
    setEditing(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((device) => {
    setEditing(device);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditing(null);
  }, []);

  const handleSave = useCallback(async (form) => {
    if (editing) {
      await saveDevice(editing.id, form);
    } else {
      await addDevice(form);
    }
  }, [editing, saveDevice, addDevice]);

  const handleDeassign = useCallback((device) => {
    const accountLabel = getAccountName(device) || "this account";
    requestDelete({
      title: "Deassign device",
      description: `Remove ${device.deviceId} from ${accountLabel}? The device stays in inventory and can be assigned again later.`,
      confirmText: device.deviceId,
      entityLabel: "Device ID",
      confirmLabel: "Deassign device",
      onConfirm: () => deassignDevice(device.id),
    });
  }, [deassignDevice, getAccountName, requestDelete]);

  const handleAssignSave = useCallback(async (accountId) => {
    if (!assignTarget || !assignDevice) return;
    await assignDevice(assignTarget.id, accountId);
  }, [assignDevice, assignTarget]);

  const handleAssignExistingSave = useCallback(async (deviceId) => {
    if (!assignExistingDevice) return;
    await assignExistingDevice(deviceId);
  }, [assignExistingDevice]);

  const showEmpty = !loading && allItems.length === 0;
  const accountName = selected ? getAccountName(selected) : "";
  const canAssignExisting = Boolean(fixedAccountId && assignExistingDevice);

  return (
    <>
      <div style={{ display: "flex", flex: 1, minHeight: 0, height: "100%" }}>
        <DeviceSidebar
          devices={items}
          selectedId={selectedId}
          onSelect={setSelectedId}
          search={search}
          onSearchChange={setSearch}
          onCreate={openCreate}
          onAssignExisting={canAssignExisting ? () => setAssignExistingOpen(true) : undefined}
          loading={loading}
          getDeviceTypeName={getDeviceTypeName}
        />
        {showEmpty ? (
          <DevicesEmptyState onCreate={openCreate} hint={emptyHint} title={emptyTitle} />
        ) : (
          <DeviceDetailPanel
            device={selected}
            deviceType={selectedDeviceType}
            sim={selectedSim}
            accountName={accountName}
            onEdit={openEdit}
            onDelete={removeDevice}
            onAssign={assignDevice ? (device) => setAssignTarget(device) : undefined}
            onDeassign={deassignDevice ? handleDeassign : undefined}
          />
        )}
      </div>
      <CreateDeviceModal
        open={modalOpen}
        onClose={closeModal}
        onSave={handleSave}
        editing={editing}
        saving={saving}
        fixedAccountId={fixedAccountId}
        deviceTypes={deviceTypes}
        sims={sims}
      />
      <AssignDeviceAccountModal
        open={Boolean(assignTarget)}
        onClose={() => setAssignTarget(null)}
        onSave={handleAssignSave}
        device={assignTarget}
        accounts={accounts}
        saving={saving}
      />
      <AssignExistingDeviceModal
        open={assignExistingOpen}
        onClose={() => setAssignExistingOpen(false)}
        onSave={handleAssignExistingSave}
        accountName={fixedAccountName ?? "account"}
        devices={unassignedDevices}
        saving={saving}
        getDeviceTypeName={getDeviceTypeName}
      />
      <DeleteConfirmModal {...deleteModalProps} />
    </>
  );
}
